import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock, Mail, Ban, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import InputRestrictionsDialog from "@/components/input-restrictions-dialog";
import TermsOfServiceDialog from "@/components/terms-of-service-dialog";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { useLogin } from "@/hooks/useLogin";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const registerSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string()
    .email("Invalid email address")
    .refine((val) => {
      const tld = val.split('.').pop() || '';
      return !(tld.length > 2 && /([a-zA-Z])\1/.test(tld));
    }, "Invalid email format (e.g. .comm instead of .com)"),
  acceptedTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the Terms of Service to create an account",
  }),
});

type RegisterData = z.infer<typeof registerSchema>;

type LoginStep = "email" | "password" | "magic-sent";

interface LoginPageProps {
  onLogin: (token: string, playerId?: string) => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const { t } = useTranslation();
  const [showRestrictionsDialog, setShowRestrictionsDialog] = useState(false);
  const [restrictionMessage, setRestrictionMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // État du login email-first (2 temps)
  const [loginStep, setLoginStep] = useState<LoginStep>("email");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginEmailError, setLoginEmailError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  const registerForm = useForm<RegisterData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { firstName: "", lastName: "", email: "", acceptedTerms: false },
  });

  const {
    activeTab,
    setActiveTab,
    showTermsDialog,
    setShowTermsDialog,
    showSuspensionDialog,
    setShowSuspensionDialog,
    suspensionData,
    loginMutation,
    checkEmailMutation,
    requestMagicMutation,
    signupMutation,
    guestLoginMutation,
    handleSignupSubmit,
  } = useLogin({
    onLogin,
    setRestrictionMessage,
    setShowRestrictionsDialog,
  });

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setLoginEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Étape 1 : email saisi → check si le compte a un mot de passe
  const handleContinueEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginEmailError("");
    if (!isValidEmail(loginEmail)) {
      setLoginEmailError(t('auth.invalidEmail', 'Adresse email invalide'));
      return;
    }
    if (rememberMe) localStorage.setItem("rememberedEmail", loginEmail);
    else localStorage.removeItem("rememberedEmail");

    try {
      const result = await checkEmailMutation.mutateAsync(loginEmail);
      if (result.hasPassword) {
        setLoginStep("password");
      } else {
        // Pas de mot de passe (compte passwordless ou inconnu) → magic link
        requestMagicMutation.mutate(loginEmail);
        setLoginStep("magic-sent");
      }
    } catch {
      // en cas d'erreur réseau, proposer quand même le magic link
      requestMagicMutation.mutate(loginEmail);
      setLoginStep("magic-sent");
    }
  };

  // Étape 2a : login classique
  const handlePasswordLogin = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email: loginEmail, password: loginPassword });
  };

  // Bascule vers magic link depuis l'écran mot de passe
  const handleUseMagicInstead = () => {
    requestMagicMutation.mutate(loginEmail);
    setLoginStep("magic-sent");
  };

  const resetLoginFlow = () => {
    setLoginStep("email");
    setLoginPassword("");
    setShowPassword(false);
  };

  const handleRegister = registerForm.handleSubmit((data) => handleSignupSubmit({ ...data, turnstileToken }));

  // Watch for live FTT Check
  const watchFirstName = registerForm.watch("firstName");
  const watchLastName = registerForm.watch("lastName");
  const [debouncedNames, setDebouncedNames] = useState({ firstName: "", lastName: "" });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNames({ firstName: watchFirstName, lastName: watchLastName });
    }, 500);
    return () => clearTimeout(timer);
  }, [watchFirstName, watchLastName]);

  const fttCheckQuery = useQuery({
    queryKey: ["fttCheck", debouncedNames.firstName, debouncedNames.lastName],
    queryFn: async () => {
      if (!debouncedNames.firstName || !debouncedNames.lastName || debouncedNames.firstName.length < 2 || debouncedNames.lastName.length < 2) {
        return null;
      }
      const res = await apiRequest("GET", `/api/auth/check-ftt?firstName=${encodeURIComponent(debouncedNames.firstName)}&lastName=${encodeURIComponent(debouncedNames.lastName)}`);
      return res.json();
    },
    enabled: activeTab === "register" && !!debouncedNames.firstName && !!debouncedNames.lastName && debouncedNames.firstName.length >= 2 && debouncedNames.lastName.length >= 2,
  });

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-2 sm:px-4 py-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-padel-green flex items-center justify-center">
            <img src="/logo.png" alt="PadELO Logo" className="h-16 sm:h-20 w-auto drop-shadow-sm mb-2" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetLoginFlow(); }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register">{t('auth.register')}</TabsTrigger>
            </TabsList>

            {/* ─── LOGIN (email-first) ─── */}
            <TabsContent value="login" className="space-y-4">
              {loginStep === "email" && (
                <form onSubmit={handleContinueEmail} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('auth.email')}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <Input
                        type="email"
                        autoComplete="username"
                        placeholder={t('auth.enterEmail')}
                        className="pl-10"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        data-testid="input-login-email"
                        autoFocus
                      />
                    </div>
                    {loginEmailError && <p className="text-sm text-red-500">{loginEmailError}</p>}
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="rememberMe" checked={rememberMe} onCheckedChange={(v) => setRememberMe(!!v)} />
                    <label htmlFor="rememberMe" className="text-sm cursor-pointer select-none">
                      {t('auth.rememberMe')}
                    </label>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-padel-green text-white hover:bg-padel-green-light"
                    disabled={checkEmailMutation.isPending}
                    data-testid="button-continue"
                  >
                    {checkEmailMutation.isPending ? t('auth.signingIn') : t('auth.continue', 'Continuer')}
                  </Button>
                </form>
              )}

              {loginStep === "password" && (
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <button type="button" onClick={resetLoginFlow} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                    <ArrowLeft size={14} /> {loginEmail}
                  </button>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('auth.password')}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <Input
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder={t('auth.enterPassword')}
                        className="pl-10 pr-10"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        data-testid="input-login-password"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-padel-green text-white hover:bg-padel-green-light"
                    disabled={loginMutation.isPending}
                    data-testid="button-login"
                  >
                    {loginMutation.isPending ? t('auth.signingIn') : t('auth.signIn')}
                  </Button>

                  <div className="text-center text-sm">
                    <button type="button" onClick={handleUseMagicInstead} className="text-blue-600 hover:underline">
                      {t('auth.loginWithMagic', 'Recevoir un lien de connexion par email')}
                    </button>
                  </div>
                </form>
              )}

              {loginStep === "magic-sent" && (
                <div className="space-y-4 text-center py-4">
                  <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                    <Mail className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold">{t('auth.magicSentTitle', 'Vérifie tes emails 📩')}</h3>
                  <p className="text-sm text-gray-600 px-4">
                    {t('auth.magicSentDesc', 'Si un compte existe pour {{email}}, un lien de connexion vient de t\'être envoyé. Il est valable 7 jours.', { email: loginEmail })}
                  </p>
                  <div className="flex flex-col gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => requestMagicMutation.mutate(loginEmail)}
                      disabled={requestMagicMutation.isPending}
                    >
                      {requestMagicMutation.isPending ? t('auth.verifying') : t('auth.resendLink', 'Renvoyer le lien')}
                    </Button>
                    <button type="button" onClick={resetLoginFlow} className="text-sm text-gray-500 hover:text-gray-700">
                      {t('auth.backToEmail', 'Utiliser une autre adresse')}
                    </button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ─── REGISTER (passwordless) ─── */}
            <TabsContent value="register" className="space-y-4">
              <Form {...registerForm}>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={registerForm.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.firstName')}</FormLabel>
                          <FormControl>
                            <Input {...field} autoComplete="given-name" placeholder={t('auth.firstName')} data-testid="input-register-firstName" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.lastName')}</FormLabel>
                          <FormControl>
                            <Input {...field} autoComplete="family-name" placeholder={t('auth.lastName')} data-testid="input-register-lastName" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="bg-blue-50/50 p-3 rounded-md border border-blue-100 flex flex-col gap-2">
                    <p className="text-xs text-blue-800">ℹ️ {t('auth.fttAssimilationInfo')}</p>
                    {watchFirstName && watchLastName && watchFirstName.length >= 2 && watchLastName.length >= 2 && (
                      <div className="flex items-center gap-2 text-sm mt-1 font-medium pb-1">
                        {fttCheckQuery.isFetching ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                            <span className="text-gray-600">{t('auth.fttSearching')}</span>
                          </>
                        ) : fttCheckQuery.data?.found ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <span className="text-green-700">{t('auth.fttFound', { elo: fttCheckQuery.data.assimilatedElo })}</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-4 w-4 text-orange-500" />
                            <span className="text-orange-700">{t('auth.fttNotFound')}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <FormField
                    control={registerForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('auth.email')}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <Input {...field} type="email" autoComplete="email" placeholder={t('auth.enterEmail')} className="pl-10" data-testid="input-register-email" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <p className="text-xs text-gray-500">
                    {t('auth.passwordlessHint', 'Pas de mot de passe à créer : tu recevras un email pour confirmer ton compte. Tu pourras définir un mot de passe plus tard dans ton profil.')}
                  </p>

                  <FormField
                    control={registerForm.control}
                    name="acceptedTerms"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-accept-terms" />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-normal cursor-pointer">
                            {t('auth.termsLabel')}{" "}
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); setShowTermsDialog(true); }}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              {t('auth.termsOfUse')}
                            </button>
                            {" "}{t('auth.andOur')}{" "}
                            <a href="https://web.padelo.fr/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                              {t('auth.privacyPolicy')}
                            </a>.
                          </FormLabel>
                          <p className="text-xs text-muted-foreground">{t('auth.commitMessage')}</p>
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Jeton à usage unique : nouveau widget après chaque envoi */}
                  <TurnstileWidget key={signupMutation.submittedAt} onToken={setTurnstileToken} />

                  <Button
                    type="submit"
                    className="w-full bg-padel-green text-white hover:bg-padel-green-light"
                    disabled={signupMutation.isPending}
                    data-testid="button-register"
                  >
                    {signupMutation.isPending ? t('auth.creatingAccount') : t('auth.createAccount')}
                  </Button>
                </form>
              </Form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 flex flex-col items-center gap-4">
            <div className="w-full flex items-center gap-4">
              <div className="h-px bg-gray-200 flex-1" />
              <span className="text-sm text-gray-500">ou</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>

            <Button
              variant="outline"
              className="w-full border-2 hover:bg-muted text-foreground"
              onClick={() => guestLoginMutation.mutate()}
              disabled={guestLoginMutation.isPending}
            >
              {guestLoginMutation.isPending ? t('auth.guestLoginPending') : t('auth.guestMode')}
            </Button>
            <p className="text-xs text-gray-500 text-center px-4">{t('auth.guestModeDesc')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Input Restrictions Dialog */}
      <InputRestrictionsDialog
        open={showRestrictionsDialog}
        onOpenChange={setShowRestrictionsDialog}
        onConfirm={() => setShowRestrictionsDialog(false)}
        message={restrictionMessage}
      />

      {/* Terms of Service Dialog */}
      <TermsOfServiceDialog open={showTermsDialog} onOpenChange={setShowTermsDialog} />

      {/* Account Suspension Dialog */}
      <Dialog open={showSuspensionDialog} onOpenChange={setShowSuspensionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" />
              {t('auth.suspension.title')}
            </DialogTitle>
            <DialogDescription>{t('auth.suspension.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {suspensionData?.reason && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">{t('auth.suspension.reason')}</p>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">{suspensionData.reason}</p>
              </div>
            )}
            {suspensionData?.suspendedUntil && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">{t('auth.suspension.expires')}</p>
                <p className="text-sm text-gray-600">
                  {new Date(suspensionData.suspendedUntil).toLocaleDateString(i18n.language, {
                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
            )}
            {!suspensionData?.suspendedUntil && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">{t('auth.suspension.type')}</p>
                <p className="text-sm text-gray-600">{t('auth.suspension.permanent')}</p>
              </div>
            )}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-4">
              <p className="text-sm text-blue-800">
                {t('auth.suspension.appeal')}{" "}
                <a href="mailto:contact@padelo.fr" className="font-semibold underline hover:text-blue-900">contact@padelo.fr</a>.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSuspensionDialog(false)} className="w-full">{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
